import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { seoHead } from "@/lib/seo";
import logoAsset from "@/assets/coffman-cove-logo.png.asset.json";
import { useScheduledReload } from "@/lib/use-scheduled-reload";
import {
  fetchTides,
  fetchMarineWeather,
  type TidePrediction,
} from "@/lib/fishing-data";
import { SafeAreaOverlay } from "@/components/SafeAreaOverlay";
import { DisplayBoardFrame } from "@/components/DisplayBoardFrame";

export const Route = createFileRoute("/display/tides")({
  validateSearch: (search: Record<string, unknown>) => ({
    safe:
      search.safe === "1" ||
      search.safe === 1 ||
      search.safe === true ||
      search.safe === "true",
  }),
  head: () =>
    seoHead({
      path: "/display/tides",
      title: "Today's Tides — Coffman Cove Display Board",
      description:
        "Fixed 1080x1920 signage board showing today's high and low tides for Coffman Cove, Alaska.",
    }),
  component: TideDisplay,
});

const TZ = "America/Anchorage";

/* Fixed signage palette — identical to /display/weather. */
const BG = "#0A0C10";
const FG = "#F7F9FB";
const LABEL = "#C3CCD6";
const ACCENT = "#FFC24D"; // next tide only
// How far past a tide extreme we still show it (marked approximate).
const TIDE_GRACE_MS = 6 * 60 * 60_000;

const RULE = "#2C3540";

const SYSTEM_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function akDateKey(iso: string) {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
}

function akTime(iso: string | number) {
  return new Date(iso).toLocaleTimeString("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit",
  });
}

function akTimeShort(iso: string | number) {
  return akTime(iso).replace(/\s?[AP]M$/, "");
}

/**
 * Slack window around a tide extreme.
 * Width scales with the interval to the neighbouring extreme and inversely
 * with the tidal range: small ranges stay slack longer, big swings less.
 * Clamped to a practical 30–120 minute window and centred on the extreme.
 */
function slackWindow(all: TidePrediction[], index: number) {
  const t = all[index];
  if (!t) return null;
  const ms = new Date(t.t).getTime();
  const neighbours = [all[index - 1], all[index + 1]].filter(Boolean) as TidePrediction[];
  if (neighbours.length === 0) return null;

  const intervals = neighbours.map((n) => Math.abs(new Date(n.t).getTime() - ms) / 60_000);
  const interval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const ranges = neighbours.map((n) => Math.abs(Number(t.v) - Number(n.v)));
  const range = Math.max(ranges.reduce((a, b) => a + b, 0) / ranges.length, 1.5);

  const minutes = Math.min(120, Math.max(30, Math.round((interval / 6) * (6 / range))));
  const half = (minutes / 2) * 60_000;
  return { minutes, start: ms - half, end: ms + half };
}


/** Last 24 hours of hourly barometric pressure, oldest → newest. */
function pressureSeries(
  hourly: { time?: string[]; pressure_msl?: number[] } | undefined,
  now: number,
) {
  const times = hourly?.time ?? [];
  const values = hourly?.pressure_msl ?? [];
  if (times.length === 0 || values.length === 0) return null;

  const cutoff = now - 24 * 60 * 60_000;
  const points: { t: number; v: number }[] = [];
  for (let i = 0; i < times.length; i += 1) {
    const t = new Date(times[i]).getTime();
    const v = Number(values[i]);
    if (!Number.isFinite(t) || !Number.isFinite(v)) continue;
    if (t >= cutoff && t <= now) points.push({ t, v });
  }
  if (points.length < 2) return null;

  const first = points[0].v;
  const last = points[points.length - 1].v;
  const delta = last - first;
  const trend = delta > 0.7 ? "RISING" : delta < -0.7 ? "FALLING" : "STEADY";
  const min = Math.min(...points.map((p) => p.v));
  const max = Math.max(...points.map((p) => p.v));
  return { points, first, last, delta, trend, min, max };
}

function TideDisplay() {
  const { safe } = Route.useSearch();
  // Hard-refresh the board at 5am, 1pm and 9pm Alaska time.
  useScheduledReload();

  const tides = useQuery({
    queryKey: ["tides", "display"],
    queryFn: ({ signal }) => fetchTides(signal),
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const weather = useQuery({
    queryKey: ["marine-weather", "display-tides"],
    queryFn: ({ signal }) => fetchMarineWeather(signal),
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const now = Date.now();
  const baro = pressureSeries(weather.data?.hourly, now);

  const todayKey = new Date(now).toLocaleDateString("en-CA", { timeZone: TZ });
  const all: TidePrediction[] = [...(tides.data ?? [])].sort(
    (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime(),
  );
  // Relaxed gating: prefer today's predictions, but if the feed hasn't rolled
  // over yet fall back to the most recent extremes within the grace window and
  // label them as approximate instead of showing "Tides Unavailable".
  const todaysExact: TidePrediction[] = all
    .filter((t) => akDateKey(t.t) === todayKey)
    .slice(0, 4);
  const recent: TidePrediction[] = all
    .filter((t) => {
      const ms = new Date(t.t).getTime();
      return ms <= now && now - ms <= TIDE_GRACE_MS;
    })
    .slice(-4);
  const approximate = todaysExact.length === 0 && recent.length > 0;
  const todays: TidePrediction[] = todaysExact.length > 0 ? todaysExact : recent;

  const slackFor = (t: TidePrediction) =>
    slackWindow(all, all.findIndex((x) => x.t === t.t));

  const nextExact = todays.find((t) => new Date(t.t).getTime() > now) ?? null;
  const next = nextExact ?? (approximate ? (todays[todays.length - 1] ?? null) : null);
  const nextSlack = next ? slackFor(next) : null;
  const unavailable = todays.length === 0;



  const lastUpdated = tides.dataUpdatedAt
    ? `${akTime(tides.dataUpdatedAt)} AK`
    : "unavailable";

  const heading = new Date(now).toLocaleDateString("en-US", {
    timeZone: TZ,
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <DisplayBoardFrame>
    <div
      style={{
        width: 1080,
        height: 1920,
        background: BG,
        color: FG,
        fontFamily: SYSTEM_FONT,
        padding: "96px 54px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* 1. Logo + place + last updated */}
      <div style={{ textAlign: "center" }}>
        <img
          src={logoAsset.url}
          alt="Coffman Cove Lodging"
          width={360}
          style={{ height: 180, width: "auto", margin: "0 auto 20px", display: "block" }}
        />

        <div style={{ fontSize: 84, fontWeight: 700, letterSpacing: 2 }}>
          Coffman Cove
        </div>
        <div style={{ fontSize: 40, fontWeight: 500, color: LABEL, marginTop: 16 }}>
          {heading} · Updated {lastUpdated}
        </div>
      </div>

      <div style={{ height: 3, background: RULE, margin: "32px 0" }} />

      {/* 2. Hero: next tide, or unavailable state */}
      <div style={{ textAlign: "center" }}>
        {unavailable ? (
          <div style={{ fontSize: 108, fontWeight: 700, lineHeight: 1.05 }}>
            Tides
            <br />
            Unavailable
          </div>
        ) : next ? (
          <>
            <div style={{ fontSize: 48, fontWeight: 600, color: LABEL }}>
              {nextExact
                ? next.type === "H"
                  ? "NEXT HIGH TIDE"
                  : "NEXT LOW TIDE"
                : next.type === "H"
                  ? "LAST HIGH TIDE (APPROX.)"
                  : "LAST LOW TIDE (APPROX.)"}
            </div>

            <div
              style={{
                fontSize: 240,
                fontWeight: 700,
                lineHeight: 1,
                color: ACCENT,
                fontVariantNumeric: "tabular-nums",
                marginTop: 8,
                whiteSpace: "nowrap",
              }}
            >
              {akTime(next.t).replace(/\s?[AP]M$/, "")}
              <span style={{ fontSize: 100, fontWeight: 700, marginLeft: 16 }}>
                {akTime(next.t).slice(-2)}
              </span>
            </div>

            <div style={{ fontSize: 84, fontWeight: 600, marginTop: 8 }}>
              {Number(next.v).toFixed(1)} ft
            </div>

            {nextSlack ? (
              <div
                style={{
                  fontSize: 56,
                  fontWeight: 600,
                  color: LABEL,
                  marginTop: 16,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                SLACK {akTimeShort(nextSlack.start)}–{akTime(nextSlack.end)} ·{" "}
                {nextSlack.minutes} MIN
              </div>
            ) : null}

          </>
        ) : (
          <>
            <div style={{ fontSize: 48, fontWeight: 600, color: LABEL }}>
              ALL TIDES COMPLETE
            </div>
            <div style={{ fontSize: 108, fontWeight: 700, marginTop: 16 }}>
              Next tide tomorrow
            </div>
          </>
        )}
      </div>

      <div style={{ height: 3, background: RULE, margin: "32px 0" }} />

      {/* 3. Today's high/low rows (max 4) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {unavailable ? (
          <div
            style={{
              fontSize: 48,
              fontWeight: 500,
              color: LABEL,
              textAlign: "center",
            }}
          >
            NOAA tide predictions have not been received. Check back after the next
            update.
          </div>
        ) : (
          <>
            {approximate && (
              <div
                style={{
                  fontSize: 40,
                  fontWeight: 600,
                  letterSpacing: 2,
                  color: LABEL,
                  textAlign: "center",
                }}
              >
                ~ APPROXIMATE · LATEST AVAILABLE PREDICTIONS
              </div>
            )}
            {todays.map((t) => {
              const s = slackFor(t);
              return (
                <Row
                  key={t.t}
                  label={`${t.type === "H" ? "High" : "Low"} · ${Number(t.v).toFixed(1)} ft`}
                  sub={
                    s
                      ? `Slack ${akTimeShort(s.start)}–${akTime(s.end)} · ${s.minutes} min`
                      : undefined
                  }
                  value={akTime(t.t)}
                  highlight={next?.t === t.t}
                />
              );
            })}
          </>
        )}

      </div>

      <div style={{ flex: 1, minHeight: 32 }} />

      {/* 4. Barometer trend, last 24 hours */}
      {baro ? <BarometerTrend baro={baro} /> : null}

      {/* 5. Source line */}

      <div
        style={{
          fontSize: 40,
          fontWeight: 500,
          color: LABEL,
          textAlign: "center",
        }}
      >
        NOAA tide predictions · barometer via Open-Meteo · all times Alaska time
      </div>

      {safe ? <SafeAreaOverlay /> : null}
    </div>
    </DisplayBoardFrame>
  );
}

function BarometerTrend({
  baro,
}: {
  baro: NonNullable<ReturnType<typeof pressureSeries>>;
}) {
  const W = 972;
  const H = 180;
  const pad = 12;
  const span = Math.max(baro.max - baro.min, 2);
  const t0 = baro.points[0].t;
  const tSpan = Math.max(baro.points[baro.points.length - 1].t - t0, 1);
  const d = baro.points
    .map((p, i) => {
      const x = pad + ((p.t - t0) / tSpan) * (W - pad * 2);
      const y = pad + (1 - (p.v - baro.min) / span) * (H - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <div style={{ marginTop: 24, borderTop: `3px solid ${RULE}`, paddingTop: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: 44, fontWeight: 600, color: LABEL }}>
          BAROMETER · 24 HR
        </span>
        <span
          style={{
            fontSize: 64,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {baro.last.toFixed(0)} hPa{" "}
          <span style={{ color: ACCENT }}>
            {baro.trend} {baro.delta >= 0 ? "+" : ""}
            {baro.delta.toFixed(1)}
          </span>
        </span>
      </div>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Barometric pressure over the last 24 hours, ${baro.trend.toLowerCase()} ${baro.delta.toFixed(1)} hectopascals`}
        style={{ display: "block", marginTop: 12 }}
      >
        <path d={d} fill="none" stroke={ACCENT} strokeWidth={6} strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 36,
          fontWeight: 500,
          color: LABEL,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span>{akTime(baro.points[0].t)}</span>
        <span>
          {baro.min.toFixed(0)}–{baro.max.toFixed(0)} hPa
        </span>
        <span>{akTime(baro.points[baro.points.length - 1].t)}</span>
      </div>
    </div>
  );
}



function Row({
  label,
  sub,
  value,
  highlight,
}: {
  label: string;
  sub?: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        borderBottom: `3px solid ${highlight ? ACCENT : RULE}`,
        paddingBottom: 18,
      }}
    >
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 48, fontWeight: 500, color: LABEL }}>{label}</span>
        {sub ? (
          <span
            style={{
              fontSize: 36,
              fontWeight: 500,
              color: LABEL,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {sub}
          </span>
        ) : null}
      </span>

      <span
        style={{
          fontSize: 100,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          color: highlight ? ACCENT : FG,
        }}
      >
        {value}
      </span>
    </div>
  );
}
